import React, { useContext, useEffect, useRef } from 'react';
import { Button } from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';
import { useNavigate, useLocation } from 'react-router-dom';

import HashState from '../../variables/hash';
import { getPatientInfo } from '../../EMR_Integration/react_webInjector.ts';
import { logGAEvent } from '../../api/googleAnalytics';
import { createNewVisitTool } from './Synapse/NewConversationButtonTool.js';

function NewConversationButton({ id = 'new-conversation-button-navbar' }) {
  const { apiClient } = useContext(GlobalContext);
  const navigate = useNavigate();
  const location = useLocation();
  let appcuesTargetId = id;

  if (window.innerWidth < 768) {
    if (id === 'new-conversation-button-sidebar') {
      appcuesTargetId = 'new-conversation-button-navbar';
    } else if (id === 'new-conversation-button-navbar') {
      appcuesTargetId = 'new-conversation-button-navbar-hidden';
    }
  }

  /**
   * Tool Magic
   */
  useEffect(() => {
    // Initialize the OpenAIAssistant
    const newTool = createNewVisitTool(newConversationRequest);
    apiClient.addTools(newTool);
    return () => {
      // Unregister the tool magic with the API client
      apiClient.removeTools(newTool.name);
    };
  }, []);

  // get the current patient name if any
  const hash = HashState();
  const hashRef = useRef(hash);
  useEffect(() => {
    hashRef.current = hash;
  }, [hash]);

  function newConversationRequest() {
    // console.log("New Conversation")
    // If they are looking at a patient, we will try to create a conversation with that patient
    if (hashRef.current !== '' && location.pathname === '/User/conversations') {
      // Hash is the patientID of the patient we are looking at
      createConversation(hashRef.current);
    } else {
      getPatientInfo()
        .then((data) => {
          // console.log("Patient Info")
          // console.log(data);
          if (!data) {
            createConversation();
          } else {
            // We were able to get the patient details from the EMR,
            // so we can create a conversation with the patient
            // Check if the patient exists in the database
            // We will provide Name, DOB, and EMR ID
            // If we see a patient with matching EMR ID we are golden
            // If we see a patient with the right name and DOB we will assume this is the right person and return their EMR ID
            // force the DOB to be in the right format, YYYY-MM-DD, it is currently M/D/YYYY
            let dob = new Date(data['DOB']);
            dob = dob.toISOString().split('T')[0]; // Extracts the date part from the ISO string
            apiClient
              .patientEMRQuery(
                data['First Name'],
                data['Last Name'],
                dob,
                data['EMR ID'],
                data['Metadata'] || ''
              )
              .then((data) => {
                // The server created a new patient if we needed, in all cases it returned the patient ID
                // console.log("Patient ID")
                // console.log(data);
                // We found a patient with the same EMR ID
                // We can create a conversation with this patient
                createConversation(data['uid']);
              })
              .catch((error) => {
                // probably no session key
                console.error('Did not get patient ID...');
                console.error(error);
                alert('Error creating conversation, Try again.');
              });
          }
        })
        .catch((error) => {
          // If there is an error with the injector we will just create a conversation without a patient
          createConversation();
        });
    }
  }

  function createConversation(patientID) {
    // Create a new conversation using the provided patient if if there is one
    apiClient
      .newConversation(patientID)
      .then((data) => {
        // Redirect to the new conversation
        // console.log("Created conversation")
        console.log(data);
        if (data.uid) {
          navigate('/User/conversations/details#' + data.uid);
          logGAEvent('new_conversation', apiClient, {
            conversation_id: data.uid
          });
        } else if (
          data.error &&
          data.error.includes('User does not have an active subscription.')
        ) {
          console.log('Subscription expired');
          alert(
            'Your trial/subscription has ended. Please subscribe to continue.'
          );
        } else {
          alert('Error creating conversation, Try again.');
        }
      })
      .catch((error) => {
        // probably no session key
        console.error('Did not create conversation');
        console.error(error);
      });
  }

  return (
    <div id={appcuesTargetId}>
      <Button
        className="d-flex align-items-center gap-2 bg-primary text-white shadow"
        style={{
          padding: '8px 16px',
          fontWeight: '500',
          fontSize: '1rem',
          borderRadius: '999px',
          marginLeft: '12px'
        }}
        onClick={newConversationRequest}
        title="New Visit"
      >
        <i
          className="ni ni-fat-add"
          style={{ fontSize: '1.1rem', marginRight: '2px' }}
        />
        <span style={{ marginRight: '2px' }}>New Visit</span>
      </Button>
    </div>
  );
}

export default NewConversationButton;
